import React, { createContext, useContext, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Category, Habit, MonthlyHabitData, DayEntry, HabitType } from '../types';
import { useAuth } from './AuthContext';
import { useSelectedDate } from './SelectedDateContext';
import { useCategories } from '../hooks/useCategories';
import { useEntries } from '../hooks/useEntries';
import { monthDateRange } from '../utils/dateUtils';

interface HabitContextType {
  categories: Category[];
  isLoading: boolean;
  currentMonth: number;
  currentYear: number;
  monthlyData: Record<string, MonthlyHabitData>;
  yearlyData: Record<string, number[]>;
  toggleHabit: (habitId: string, day: number) => void;
  toggleHabitOn: (habitId: string, day: number, month: number, year: number) => void;
  setRating: (habitId: string, day: number, rating: number) => void;
  setRatingOn: (habitId: string, day: number, rating: number, month: number, year: number) => void;
  setText: (habitId: string, day: number, text: string) => void;
  setTextOn: (habitId: string, day: number, text: string, month: number, year: number) => void;
  getHabitEntries: (habitId: string) => DayEntry[];
  getHabitEntriesFor: (habitId: string, month: number, year: number) => DayEntry[];
  getHabitMonthlyPercentage: (habitId: string) => number;
  navigateMonth: (direction: 'prev' | 'next') => void;
  updateHabitType: (habitId: string, type: HabitType) => void | Promise<void>;
  updateHabitColor: (habitId: string, color: string) => void | Promise<void>;
  updateHabitScheduledTime: (habitId: string, scheduledTime: string | undefined) => void | Promise<void>;
  addHabit: (name: string, type: HabitType, color: string, temporary?: boolean, scheduledTime?: string) => Promise<string>;
  removeHabit: (habitId: string) => void;
  renameHabit: (habitId: string, name: string) => void | Promise<void>;
  reorderHabits: (fromId: string, toId: string) => void;
  canUndo: boolean;
  undoRemoveHabit: () => void;
}

const HabitContext = createContext<HabitContextType | undefined>(undefined);

export const HabitProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userId } = useAuth();
  const { selectedDate } = useSelectedDate();
  const location = useLocation();
  const now = new Date();
  const [currentMonth, setCurrentMonth] = useState(now.getMonth());
  const [currentYear, setCurrentYear] = useState(now.getFullYear());
  const [undoStack, setUndoStack] = useState<Array<{ habit: Habit; categoryId: string; index: number }>>([]);

  // ── Data layer ──────────────────────────────────────────────────────────────
  const {
    categories, isLoading,
    syncCategories, deleteHabit,
    addHabit, renameHabit, updateHabitType, updateHabitColor, updateHabitScheduledTime, reorderHabits,
  } = useCategories(userId);

  const isStats = location.pathname.startsWith('/statistics');
  const entriesRange = isStats
    ? { from: `${currentYear}-01-01`, to: `${currentYear}-12-31` }
    : monthDateRange(selectedDate.getFullYear(), selectedDate.getMonth());
  const entriesYear = isStats ? currentYear : selectedDate.getFullYear();

  const {
    monthlyData, yearlyData, isLoading: entriesLoading,
    upsertEntry, getHabitEntries: getEntries, getHabitMonthlyPercentage: getMonthlyPct,
  } = useEntries(userId, entriesYear, categories, entriesRange);

  // ── Navigation ──────────────────────────────────────────────────────────────
  const navigateMonth = useCallback((direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
      else setCurrentMonth(m => m - 1);
    } else {
      if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
      else setCurrentMonth(m => m + 1);
    }
  }, [currentMonth]);

  // ── Entry helpers ───────────────────────────────────────────────────────────
  const toggleHabit = useCallback((habitId: string, day: number) => {
    const key = `${habitId}-${currentYear}-${currentMonth}`;
    const existing = monthlyData[key]?.entries.find(e => e.day === day);
    const nowCompleting = !(existing?.completed ?? false);
    upsertEntry(habitId, currentYear, currentMonth, day, {
      completed: nowCompleting,
      completedAt: nowCompleting ? new Date().toISOString() : null,
    });
  }, [currentMonth, currentYear, monthlyData, upsertEntry]);

  const toggleHabitOn = useCallback((habitId: string, day: number, month: number, year: number) => {
    const key = `${habitId}-${year}-${month}`;
    const existing = monthlyData[key]?.entries.find(e => e.day === day);
    const nowCompleting = !(existing?.completed ?? false);
    upsertEntry(habitId, year, month, day, {
      completed: nowCompleting,
      completedAt: nowCompleting ? new Date().toISOString() : null,
    });
  }, [monthlyData, upsertEntry]);

  const setRating = useCallback((habitId: string, day: number, rating: number) => {
    const key = `${habitId}-${currentYear}-${currentMonth}`;
    const existing = monthlyData[key]?.entries.find(e => e.day === day);
    const newRating = existing?.rating === rating ? 0 : rating;
    upsertEntry(habitId, currentYear, currentMonth, day, { rating: newRating || null });
  }, [currentMonth, currentYear, monthlyData, upsertEntry]);

  const setRatingOn = useCallback((habitId: string, day: number, rating: number, month: number, year: number) => {
    const key = `${habitId}-${year}-${month}`;
    const existing = monthlyData[key]?.entries.find(e => e.day === day);
    const newRating = existing?.rating === rating ? 0 : rating;
    upsertEntry(habitId, year, month, day, { rating: newRating || null });
  }, [monthlyData, upsertEntry]);

  const setText = useCallback((habitId: string, day: number, text: string) => {
    upsertEntry(habitId, currentYear, currentMonth, day, { text });
  }, [currentMonth, currentYear, upsertEntry]);

  const setTextOn = useCallback((habitId: string, day: number, text: string, month: number, year: number) => {
    upsertEntry(habitId, year, month, day, { text });
  }, [upsertEntry]);

  const getHabitEntries = useCallback((habitId: string): DayEntry[] =>
    getEntries(habitId, currentMonth, currentYear),
  [getEntries, currentMonth, currentYear]);

  const getHabitEntriesFor = useCallback((habitId: string, month: number, year: number): DayEntry[] =>
    getEntries(habitId, month, year),
  [getEntries]);

  const getHabitMonthlyPercentage = useCallback((habitId: string): number => {
    const habit = categories.flatMap(c => c.habits).find(h => h.id === habitId);
    if (!habit) return 0;
    return getMonthlyPct(habitId, currentMonth, habit.type);
  }, [categories, currentMonth, getMonthlyPct]);

  // ── Undo stack (pure UI state) ──────────────────────────────────────────────
  const removeHabit = useCallback((habitId: string) => {
    const found = categories
      .flatMap(c => c.habits.map((h, i) => ({ h, categoryId: c.id, index: i })))
      .find(x => x.h.id === habitId);
    if (found) {
      setUndoStack(s => [...s.slice(-19), { habit: found.h, categoryId: found.categoryId, index: found.index }]);
    }
    deleteHabit(habitId);
  }, [categories, deleteHabit]);

  const undoRemoveHabit = useCallback(() => {
    setUndoStack(prev => {
      if (!prev.length) return prev;
      const last = prev[prev.length - 1];
      const restored = categories.map(cat => {
        if (cat.id !== last.categoryId) return cat;
        const habits = [...cat.habits];
        habits.splice(last.index, 0, last.habit);
        return { ...cat, habits };
      });
      syncCategories(restored);
      return prev.slice(0, -1);
    });
  }, [categories, syncCategories]);

  return (
    <HabitContext.Provider value={{
      categories, isLoading: isLoading || entriesLoading,
      currentMonth, currentYear,
      monthlyData, yearlyData,
      toggleHabit, toggleHabitOn, setRating, setRatingOn, setText, setTextOn,
      getHabitEntries, getHabitEntriesFor, getHabitMonthlyPercentage,
      navigateMonth,
      updateHabitType, updateHabitColor, updateHabitScheduledTime, addHabit, removeHabit, renameHabit, reorderHabits,
      canUndo: undoStack.length > 0, undoRemoveHabit,
    }}>
      {children}
    </HabitContext.Provider>
  );
};

export const useHabitContext = () => {
  const ctx = useContext(HabitContext);
  if (!ctx) throw new Error('useHabitContext must be used within HabitProvider');
  return ctx;
};
